import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "./ui/card";
import VehicleInfoService from "../lib/VehicleInfoService";
import {
  Camera,
  Upload,
  Check,
  X,
  AlertCircle,
  ArrowLeft,
  Gauge,
  CreditCard,
} from "lucide-react";
import ExtendedWarrantyScreenWrapper, {
  ExtendedWarrantyHeader,
  ExtendedWarrantyCard,
  ExtendedWarrantyButton,
} from "./extended-warranty/ExtendedWarrantyScreenWrapper";
import { useAuth } from "../hooks/useAuth";
import ExtendedWarrantyService from "../lib/ExtendedWarrantyService";
import API_CONFIG from "../lib/ApiConfig";

const MileageCheckinScreen = ({ onComplete, onBack }) => {
  const { user, getIdToken } = useAuth();
  const [photos, setPhotos] = useState({
    odometer: null,
    license: null,
  });
  const [previews, setPreviews] = useState({
    odometer: null,
    license: null,
  });
  const [vehicleData, setVehicleData] = useState({
    vehicleId: "NEW_VEHICLE",
    licensePlate: "",
    make: "",
    makeId: "",
    makeName: "",
    model: "",
    modelId: "",
    modelName: "",
    year: "",
    mileage: "",
  });
  const [vehicleOptions, setVehicleOptions] = useState({
    makes: [],
    models: [],
    years: [],
  });
  const [loadingStates, setLoadingStates] = useState({
    makes: false,
    models: false,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Refs for file inputs
  const odometerFileRef = useRef(null);
  const odometerCameraRef = useRef(null);
  const licenseFileRef = useRef(null);
  const licenseCameraRef = useRef(null);

  // Generate year options (last 30 years)
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 30; i++) {
      years.push(currentYear - i);
    }
    setVehicleOptions((prev) => ({ ...prev, years }));
  }, []);

  // Fetch vehicle makes on component mount
  useEffect(() => {
    VehicleInfoService.fetchVehicleMakes();
  }, [user, getIdToken]);

  // Fetch models when make changes
  useEffect(() => {
    console.log("use effect");
    console.log(vehicleData);
    if (vehicleData.makeName) {
      VehicleInfoService.fetchVehicleModels(vehicleData.makeName);
    } else {
      setVehicleOptions((prev) => ({ ...prev, models: [] }));
      setVehicleData((prev) => ({
        ...prev,
        model: "",
        modelId: "",
        modelName: "",
      }));
    }
  }, [vehicleData.makeName]);

  // Detect if the user is on a mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
      setIsMobile(mobileRegex.test(userAgent));
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const validateFile = (file) => {
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB

    if (!file.type.startsWith("image/")) {
      throw new Error("Please upload an image file");
    }

    if (file.size > MAX_SIZE) {
      throw new Error("File size must be less than 10MB");
    }
  };

  const handleFileSelect = (type, file) => {
    try {
      validateFile(file);

      setPhotos((prev) => ({
        ...prev,
        [type]: file,
      }));

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviews((prev) => ({
          ...prev,
          [type]: reader.result,
        }));
      };
      reader.readAsDataURL(file);

      // Clear error for this type
      setErrors((prev) => ({
        ...prev,
        [type]: null,
      }));
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        [type]: err.message,
      }));
    }
  };

  const handleFileInput = (type, e) => {
    if (e.target.files?.[0]) {
      handleFileSelect(type, e.target.files[0]);
    }
  };

  const removePhoto = (type) => {
    setPhotos((prev) => ({
      ...prev,
      [type]: null,
    }));
    setPreviews((prev) => ({
      ...prev,
      [type]: null,
    }));
    setErrors((prev) => ({
      ...prev,
      [type]: null,
    }));
  };

  const handleVehicleDataChange = (field, value) => {
    if (field === "make") {
      // When make changes, find the selected make object and update related fields
      const selectedMake = vehicleOptions.makes.find(
        (make) => make.id.toString() === value
      );
      if (selectedMake) {
        setVehicleData((prev) => ({
          ...prev,
          makeId: selectedMake.id,
          makeName: selectedMake.name,
          make: selectedMake.name,
          // Clear model when make changes
          model: "",
          modelId: "",
          modelName: "",
        }));
      }
    } else if (field === "model") {
      // When model changes, find the selected model object and update related fields
      const selectedModel = vehicleOptions.models.find(
        (model) => model.id.toString() === value
      );
      if (selectedModel) {
        setVehicleData((prev) => ({
          ...prev,
          modelId: selectedModel.id,
          modelName: selectedModel.name,
          model: selectedModel.name,
        }));
      }
    } else {
      setVehicleData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleCameraCapture = (type) => {
    const cameraRef =
      type === "odometer" ? odometerCameraRef : licenseCameraRef;
    if (cameraRef.current) {
      cameraRef.current.click();
    }
  };

  const handleSubmit = async () => {
    // Validate both photos and vehicle data are present
    const newErrors = {};

    if (!photos.odometer) {
      newErrors.odometer = "Odometer photo is required";
    }

    if (!photos.license) {
      newErrors.license = "License plate photo is required";
    }

    if (!vehicleData.licensePlate.trim()) {
      newErrors.licensePlate = "License plate number is required";
    }

    if (!vehicleData.year.trim()) {
      newErrors.year = "Vehicle year is required";
    }

    if (!vehicleData.make.trim()) {
      newErrors.make = "Vehicle make is required";
    }

    if (!vehicleData.model.trim()) {
      newErrors.model = "Vehicle model is required";
    }

    if (!vehicleData.mileage.trim()) {
      newErrors.mileage = "Mileage reading is required";
    } else if (!/^\d+$/.test(vehicleData.mileage)) {
      newErrors.mileage = "Mileage must be a valid number";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsSubmitting(true);
      setErrors({});

      // Get Firebase token for authentication
      const token = await getIdToken();

      // Upload both photos and get URLs
      const odometerUrl = await ExtendedWarrantyService.uploadWarrantyDocument(
        photos.odometer,
        token
      );

      const licenseUrl = await ExtendedWarrantyService.uploadWarrantyDocument(
        photos.license,
        token
      );

      // Prepare the mileage check-in payload in the exact format you specified
      const mileagePayload = {
        vehicleId: vehicleData.vehicleId,
        licensePlate: vehicleData.licensePlate,
        year: parseInt(vehicleData.year),
        make: vehicleData.make,
        model: vehicleData.model,
        mileage: parseInt(vehicleData.mileage),
        licensePhotos: [
          {
            url: licenseUrl,
          },
        ],
        odometerPhotos: [
          {
            url: odometerUrl,
          },
        ],
      };

      // Submit the mileage check-in with the new payload format
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/extended-warranty/checkin/mileage`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": token,
          },
          body: JSON.stringify(mileagePayload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Server responded with status: ${response.status}`
        );
      }

      const result = await response.json();

      // Store mileage check-in data in session storage
      const checkinData = {
        type: "mileage_checkin",
        timestamp: new Date().toISOString(),
        vehicleData: vehicleData,
        photos: {
          odometer: photos.odometer.name,
          license: photos.license.name,
        },
        payload: mileagePayload,
        result: result,
      };

      sessionStorage.setItem("mileageCheckinData", JSON.stringify(checkinData));

      // Call onComplete with the check-in data
      if (onComplete) {
        onComplete(checkinData);
      }
    } catch (error) {
      console.error("Error submitting mileage check-in:", error);
      setErrors({
        general:
          error.message || "Failed to submit check-in. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPhotoCapture = (type, icon, title, description) => {
    const photo = photos[type];
    const preview = previews[type];
    const error = errors[type];
    const fileRef = type === "odometer" ? odometerFileRef : licenseFileRef;
    const cameraRef =
      type === "odometer" ? odometerCameraRef : licenseCameraRef;

    return (
      <ExtendedWarrantyCard key={type}>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              {icon}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-600">{description}</p>
            </div>
          </div>

          {!photo ? (
            <div className="space-y-3">
              {/* Hidden file inputs */}
              <input
                type="file"
                ref={fileRef}
                onChange={(e) => handleFileInput(type, e)}
                accept="image/*"
                className="hidden"
              />
              <input
                type="file"
                ref={cameraRef}
                onChange={(e) => handleFileInput(type, e)}
                accept="image/*"
                capture="environment"
                className="hidden"
              />

              {/* Upload options */}
              <div
                className={isMobile ? "grid grid-cols-1 gap-3" : "flex gap-3"}
              >
                {/* <button
                  onClick={() => fileRef.current?.click()}
                  className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <Upload className="w-6 h-6 text-blue-500" />
                  <span className="text-sm font-medium">Choose File</span>
                </button> */}

                {isMobile && (
                  <button
                    onClick={() => handleCameraCapture(type)}
                    className="flex flex-col items-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                  >
                    <Camera className="w-6 h-6 text-blue-500" />
                    <span className="text-sm font-medium">Take Photo</span>
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Photo preview */}
              <div className="relative">
                <img
                  src={preview}
                  alt={`${title} preview`}
                  className="w-full h-48 object-cover rounded-lg border"
                />
                <button
                  onClick={() => removePhoto(type)}
                  className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-lg hover:bg-gray-100"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              {/* Success indicator */}
              <div className="flex items-center gap-2 text-green-600">
                <Check className="w-4 h-4" />
                <span className="text-sm font-medium">Photo captured</span>
              </div>
            </div>
          )}

          {/* Error display */}
          {error && (
            <div className="flex items-start gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>
      </ExtendedWarrantyCard>
    );
  };

  // Calculate if form is valid for button state
  const isFormValid = () => {
    return (
      photos.odometer &&
      photos.license &&
      vehicleData.licensePlate.trim() &&
      vehicleData.year.trim() &&
      vehicleData.make.trim() &&
      vehicleData.model.trim() &&
      vehicleData.mileage.trim() &&
      /^\d+$/.test(vehicleData.mileage)
    );
  };

  return (
    <ExtendedWarrantyScreenWrapper>
      {/* Back button */}
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </button>
      )}

      {/* Header */}
      <ExtendedWarrantyHeader
        title="Mileage Check-In"
        subtitle="Take photos of your odometer and license plate to verify your vehicle information"
      />

      {/* Instructions */}
      <ExtendedWarrantyCard variant="info">
        <div className="space-y-3">
          <h4 className="font-semibold text-gray-900">Photo Guidelines:</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Ensure photos are clear and well-lit</li>
            <li>• Make sure all numbers are clearly visible</li>
            <li>• Avoid glare or shadows on the display/plate</li>
            <li>• Take photos straight-on for best results</li>
          </ul>
        </div>
      </ExtendedWarrantyCard>

      {/* Vehicle Information Form */}
      <ExtendedWarrantyCard>
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-900">Vehicle Information</h3>

          <div className="grid grid-cols-1 gap-4">
            {/* License Plate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                License Plate Number *
              </label>
              <input
                type="text"
                value={vehicleData.licensePlate}
                onChange={(e) =>
                  handleVehicleDataChange(
                    "licensePlate",
                    e.target.value.toUpperCase()
                  )
                }
                placeholder="ABC1234"
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.licensePlate ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.licensePlate && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.licensePlate}
                </p>
              )}
            </div>

            {/* Year */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Year *
              </label>
              <select
                value={vehicleData.year}
                onChange={(e) =>
                  handleVehicleDataChange("year", e.target.value)
                }
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.year ? "border-red-500" : "border-gray-300"
                }`}
              >
                <option value="">Select Year</option>
                {vehicleOptions.years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              {errors.year && (
                <p className="text-red-500 text-sm mt-1">{errors.year}</p>
              )}
            </div>

            {/* Make and Model */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Make *
                </label>
                <select
                  value={vehicleData.makeId}
                  onChange={(e) =>
                    handleVehicleDataChange("make", e.target.value)
                  }
                  disabled={loadingStates.makes}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 ${
                    errors.make ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">
                    {loadingStates.makes ? "Loading makes..." : "Select Make"}
                  </option>
                  {vehicleOptions.makes.map((make) => (
                    <option key={make.id} value={make.id}>
                      {make.name}
                    </option>
                  ))}
                </select>
                {errors.make && (
                  <p className="text-red-500 text-sm mt-1">{errors.make}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Model *
                </label>
                <select
                  value={vehicleData.modelId}
                  onChange={(e) =>
                    handleVehicleDataChange("model", e.target.value)
                  }
                  disabled={!vehicleData.makeId || loadingStates.models}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 ${
                    errors.model ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  <option value="">
                    {loadingStates.models
                      ? "Loading models..."
                      : !vehicleData.makeId
                      ? "Select make first"
                      : "Select Model"}
                  </option>
                  {vehicleOptions.models.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </select>
                {errors.model && (
                  <p className="text-red-500 text-sm mt-1">{errors.model}</p>
                )}
              </div>
            </div>

            {/* Mileage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Mileage *
              </label>
              <input
                type="number"
                value={vehicleData.mileage}
                onChange={(e) =>
                  handleVehicleDataChange("mileage", e.target.value)
                }
                placeholder="12345"
                className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.mileage ? "border-red-500" : "border-gray-300"
                }`}
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter the mileage shown in your odometer photo
              </p>
              {errors.mileage && (
                <p className="text-red-500 text-sm mt-1">{errors.mileage}</p>
              )}
            </div>
          </div>
        </div>
      </ExtendedWarrantyCard>

      {/* Photo capture sections */}
      {renderPhotoCapture(
        "odometer",
        <Gauge className="w-5 h-5 text-blue-600" />,
        "Odometer Reading",
        "Take a clear photo of your vehicle's odometer display"
      )}

      {renderPhotoCapture(
        "license",
        <CreditCard className="w-5 h-5 text-blue-600" />,
        "License Plate",
        "Take a photo of your vehicle's license plate"
      )}

      {/* General error */}
      {errors.general && (
        <ExtendedWarrantyCard variant="error">
          <div className="flex items-start gap-2 text-red-600">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <span>{errors.general}</span>
          </div>
        </ExtendedWarrantyCard>
      )}

      {/* Submit button */}
      <ExtendedWarrantyButton
        onClick={handleSubmit}
        disabled={isSubmitting || !isFormValid()}
      >
        {isSubmitting ? (
          <>
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            <span>Submitting Check-In...</span>
          </>
        ) : (
          <>
            <span>Submit Check-In</span>
            <Check className="w-4 h-4" />
          </>
        )}
      </ExtendedWarrantyButton>

      {/* Privacy note */}
      <div className="text-xs text-center text-gray-500 px-4">
        Your photos are securely processed and encrypted. We use this
        information solely for verification purposes and in accordance with our
        privacy policy.
      </div>
    </ExtendedWarrantyScreenWrapper>
  );
};

export default MileageCheckinScreen;
